import { allSlideTypes } from '@/constant/slideTypes'
import appConfig from '@/config'
import { isFeatureEnabled } from '../featureToggles.module'

const DEFAULT_CUSTOM_SLIDE_SETTING = Object.freeze({
  enableQuestionTitle: true,
  enableQuestionDescription: true,
  enableTimeLimit: true,
  enableStopSubmitssionSetting: true,
  enableHideResultSetting: true,
  enableQuestionImage: true,
  enableVoteCount: true,
  enableLabelOtherSetting: false,
  enableMultipleSubmission: false,
  enableFullScreen: false,
  enableIframePreload: false,
  // AHA-46422 keep-alive preload — ON by default for every plugin slide type
  // (the whole feature is master-gated by the `AHA-46422-preload-aha-slide-plugin`
  // flag; turn the flag off to disable everywhere). While presenting, the host
  // boots the NEXT plugin slide's iframe ahead of time with xprops.active=false
  // (bundles load, plugin stays blank), then flips active=true + pushes data on
  // navigation so the slide appears instantly with no bundle reload. Set
  // `disablePreloadPlugin: true` on a slide type's `setting` to opt it OUT.
  disablePreloadPlugin: false,
  enableGameLobby: false,
  // When true, this plugin slide type reuses the host's native quiz state
  // machine (lobby → rule → countdown → question → result). The host owns the
  // lobby/rule/countdown screens and only mounts the plugin canvas iframe at
  // QUESTION/RESULT (see isSlideActiveFullQuizPlugin). Defaults false so every
  // existing plugin keeps the old PluginGameLobby flow verbatim.
  enableQuizSetting: false,
  // AHAM-507: when true, the plugin iframe renders the question image itself
  // (Canvas.vue in slide-type-creator), so the host must NOT also paint its own
  // copy at QUESTION/RESULT — see isQuestionImageRenderedInIframe below. Fixed
  // cross-repo contract name shared verbatim with slide-type-creator and
  // stpancras-audience-app — do not rename. Defaults false: every other plugin
  // keeps the host-rendered image unchanged. Independent of enableQuestionImage,
  // which only gates the native "Your question" UPLOAD control and is untouched.
  enableQuestionImageInIframe: false,
  // When true, the host HIDES the question title component on the presenter canvas
  // even though the slide is NOT full-canvas (enableFullScreen stays false / framed).
  // Use it when a framed plugin renders the question title itself inside its iframe
  // and must not have the host paint a second copy above it. Independent of
  // enableFullScreen (which hides the title only as a side effect of taking the
  // whole canvas) — see isSlideTitleComponentVisible in SlideContent.vue. Fixed
  // cross-repo contract name shared verbatim with slide-type-creator — do not rename.
  // Defaults false: every existing plugin keeps its host-rendered title unchanged.
  enableHideQuestionTitle: false,
  // When true, the plugin canvas renders the audience "is typing" indicator itself,
  // off the `onTyping` xprop SlidePluginIframeEmbed forwards from the host socket
  // (the receive half of AHA-41641; the audience `emitTyping` half already shipped),
  // so the host must NOT also paint its own "Participants are typing" bubble over
  // the iframe - see isTypingIndicatorRenderedInIframe below and Slide.vue's
  // isActiveUserTypingFeature. Fixed cross-repo contract name shared verbatim with
  // slide-type-creator - do not rename. Defaults false: every other plugin keeps the
  // host bubble unchanged.
  enableTypingIndicatorInIframe: false,
})

const _buildConfigForType = (type, rootGetters) => {
  const builtin = allSlideTypes.find((slideType) => slideType.type === type)
  if (builtin) return builtin

  const custom = rootGetters['embedApp/customSlideTypeByType'](type)
  if (!custom) return {}

  return {
    ...custom,
    plugin: true,
    setting: { ...DEFAULT_CUSTOM_SLIDE_SETTING, ...(custom.setting || {}) },
  }
}

const _getters = {
  canUseSlidePlugin: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-39386-aha-slide-plugin') ||
      isFeatureEnabled(rootGetters, 'AHA-42884-new-slide-market-place')
  },

  slideActiveConfig: (_, __, rootState, rootGetters) => {
    const { type } = rootState.slideActive
    return _buildConfigForType(type, rootGetters)
  },

  // AHA-43282: single source of truth for "the keep-alive plugin overlay owns the
  // active slide" — the exact condition under which the AHA-46422 overlay paints
  // the plugin iframe over this slide's in-flow host chrome (result/submission
  // popups, minimised notification chips). Consumers (Slide.vue,
  // PresenterControlBarNew.vue) portal that chrome above the overlay when true, and
  // render it in place otherwise. Mirrors SlidePluginIframeEmbed.keepAliveActive so
  // the gate lives in ONE place instead of being copied per component.
  keepAliveOwnsActiveSlide: (_, getters, rootState, rootGetters) => {
    const config = getters.slideActiveConfig
    return Boolean(rootGetters['featureToggles/canUsePreloadAhaSlidePlugin'])
      && Boolean(rootGetters.isPresenting)
      && !rootState.sharePresentation
      && !rootState.isEnableAddinPowerpoint
      && Boolean(config.plugin)
      && !config.setting?.disablePreloadPlugin
      // Full-quiz plugins render inline in the native quiz machine (not via the
      // keep-alive overlay), so keep-alive never owns their active slide. Mirrors
      // the enableQuizSetting opt-out in SlidePluginIframeEmbed.keepAliveActive.
      && !config.setting?.enableQuizSetting
  },

  // Resolve the slide-type config for an arbitrary slide type (not just the
  // active one). Used by the "Apply all" settings actions to decide which
  // slides in the presentation expose the same setting.
  slideConfigByType: (_, __, ___, rootGetters) => (type) => _buildConfigForType(type, rootGetters),

  isSlideActiveFullCanvasPlugin: (_, getters, ___, rootGetters) => {
    if (!isFeatureEnabled(rootGetters, 'AHA-39386-aha-slide-plugin')) return false
    const config = getters.slideActiveConfig
    // `enableFullScreen` is the canonical manifest key (see
    // aha-slide-plugin/packages/toolkit/schema/plugin-manifest.schema.json) —
    // the host's "fullCanvas" layout is the response to that opt-in.
    return Boolean(config.plugin && config.setting?.enableFullScreen)
  },

  // True when the active FRAMED plugin slide asked the host to hide the question
  // title on the presenter canvas (setting.enableHideQuestionTitle) because the
  // plugin renders that title itself inside its iframe. Lets a framed plugin
  // (enableFullScreen false) suppress the host title WITHOUT going full-canvas —
  // SlideContent.isSlideTitleComponentVisible reads this. Defaults false, so every
  // other plugin keeps its host-rendered title.
  isSlideActiveHideQuestionTitlePlugin: (_, getters) => {
    const config = getters.slideActiveConfig
    return Boolean(config.plugin && config.setting?.enableHideQuestionTitle)
  },

  // AHAM-507: true when the active plugin slide opted the iframe into rendering
  // the question image itself (setting.enableQuestionImageInIframe). Consumers
  // (SlidePluginIframeCanvas) use this to suppress the host's OWN render of the
  // image — otherwise it paints twice, once from the host and once from the
  // plugin iframe. Does NOT touch the native "Your question" upload control
  // (QuestionImageButton / inputQuestionImageVisible / enableQuestionImage) —
  // that stays wired to the existing enableQuestionImage flag, unchanged.
  isQuestionImageRenderedInIframe: (_, getters) => {
    const config = getters.slideActiveConfig
    return Boolean(config.plugin && config.setting?.enableQuestionImageInIframe)
  },

  // True when the active plugin slide opted into rendering the audience typing
  // indicator itself (setting.enableTypingIndicatorInIframe). Slide.vue's
  // isActiveUserTypingFeature reads this to keep the host's own CanvasUserIsTyping
  // bubble down for that slide, so one typing signal never draws two indicators.
  // Modelled on isQuestionImageRenderedInIframe above.
  isTypingIndicatorRenderedInIframe: (_, getters) => {
    const config = getters.slideActiveConfig
    return Boolean(config.plugin && config.setting?.enableTypingIndicatorInIframe)
  },

  // AHA plugin quiz: this plugin slide type reuses the host's native quiz state
  // machine. True only when the active slide is a plugin whose manifest opts in
  // via `setting.enableQuizSetting`. Consumers route the slide through
  // QuizSlideContent (host lobby/rule/countdown) and mount the plugin iframe
  // only at QUESTION/RESULT. Modelled on isSlideActiveFullCanvasPlugin below.
  isSlideActiveFullQuizPlugin: (_, getters, rootState, rootGetters) => {
    const config = getters.slideActiveConfig
    // Also require isQuizSlide (scored), matching the pre-existing isPluginQuizSlide
    // gate (SlideContent.vue). A non-scored plugin that merely sets enableQuizSetting
    // (for the host quiz-settings cluster) must NOT be pulled into the native quiz
    // machine — QuizSlideContent filters the deck by isQuizSlide, so it would count
    // 0 quizzes and render "Question 0 of 0" / broadcast questionCount:0.
    return Boolean(config.plugin && config.setting?.enableQuizSetting === true)
      && rootGetters['slideModule/isQuizSlide'](rootState.slideActive)
  },

  // Runtime variant of isSlideActiveFullCanvasPlugin: a quiz plugin always keeps
  // the instruction bar + control panel, so its lobby/iframe sits in normal flow
  // (inset, filling whatever the chrome leaves) instead of full-bleed under them.
  // Only non-quiz full-canvas plugins take the whole canvas.
  isSlideActiveFullCanvasPluginActive: (_, getters, rootState, rootGetters) => {
    if (!getters.isSlideActiveFullCanvasPlugin) return false
    return !rootGetters['slideModule/isQuizSlide'](rootState.slideActive)
  },

  // Reactive lookup of the slide that follows the active one in playback order.
  // Returns null when the active slide is the last one (or there is no active slide).
  // Skip-slide is intentionally ignored here — preload is a best-effort optimisation
  // and the skip-slide check is async; mis-preloading a skipped slide is wasted work
  // but never wrong.
  nextSlideAfterActive: (_, __, rootState) => {
    const slides = rootState.presentation?.slides
    const activeId = rootState.slideActive?.id
    if (!slides?.length || !activeId) return null
    const idx = slides.findIndex((s) => s.id === activeId)
    if (idx < 0 || idx >= slides.length - 1) return null
    return slides[idx + 1]
  },

  // URL the host can preload (plain hidden iframe) to warm browser caches for the
  // next slide's plugin canvas. Opt-in is the single switch: returns null unless
  // the next slide is a plugin slide type whose `setting.enableIframePreload` is
  // true (default false). No feature-flag gate — the per-plugin flag is enough,
  // and built-in plugin slide types (e.g. content-v2) ship under their own flags,
  // not `AHA-39386-aha-slide-plugin`.
  //
  // URL is built the same way SlidePluginIframeEmbed builds the real iframe src,
  // so the warmed URL matches the request made on navigation:
  //   - manifest plugins that ship an explicit `canvasUrl` → `${canvasUrl}/${id}`
  //   - built-in plugin types with no `canvasUrl` (content-v2, survey) →
  //     `${slidePluginUrl}/${type}/canvas/${id}`
  nextSlideIframePreloadUrl: (_, getters, __, rootGetters) => {
    const next = getters.nextSlideAfterActive
    if (!next?.type) return null
    const config = _buildConfigForType(next.type, rootGetters)
    if (!config.plugin || !config.setting?.enableIframePreload) return null
    // AHA-46422: if keep-alive already owns the next slide (flag on + not opted
    // out) it boots the real iframe itself — returning a URL here would load the
    // same canvas a second time (hidden plain iframe + live keep-alive iframe).
    // Keep this plain preload only for slides keep-alive does NOT own.
    const keepAliveOwnsNext = isFeatureEnabled(rootGetters, 'AHA-46422-preload-aha-slide-plugin')
      && !config.setting?.disablePreloadPlugin
    if (keepAliveOwnsNext) return null
    return config.canvasUrl
      ? `${config.canvasUrl}/${next.id}`
      : `${appConfig.slidePluginUrl}/${next.type}/canvas/${next.id}`
  },
}

export default _getters
